import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid2 as Grid,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import TimelineIcon from '@mui/icons-material/Timeline';
import BalanceIcon from '@mui/icons-material/Balance';
import axios from 'axios';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionPaper = motion(Paper);
const MotionGrid = motion(Grid);

interface PlaintiffsSectionProps {
  scrollToContact: () => void;
}

const PlaintiffsSection = React.forwardRef<
  HTMLDivElement,
  PlaintiffsSectionProps
>(({ scrollToContact }, ref) => {
  const theme = useTheme();
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<
    'success' | 'error'
  >('success');

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const staggerFeatures = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const featureItem = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        type: 'spring',
        stiffness: 200,
      },
    },
  };

  const features = [
    {
      icon: <DescriptionIcon fontSize="large" />,
      title: 'Easy Enrollment',
      description:
        'Simple registration process that takes minutes, not hours. No paperwork hassle.',
    },
    {
      icon: <TimelineIcon fontSize="large" />,
      title: 'Real-time Updates',
      description:
        'Stay informed with timely updates on your case status and important milestones.',
    },
    {
      icon: <BalanceIcon fontSize="large" />,
      title: 'Fair Compensation',
      description:
        'Ensure you receive the settlement you deserve without any hidden fees.',
    },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateEmail(email)) {
      setLoading(true);

      axios
        .post(process.env.REACT_APP_MAILING_LIST_ENDPOINT || '', {
          email: email,
        })
        .then(() => {
          setSnackbarSeverity('success');
          setSnackbarMessage('You have successfully joined our mailing list!');
          setSnackbarOpen(true);
          setEmail('');
        })
        .catch((error) => {
          console.error(error);
          setSnackbarSeverity('error');
          setSnackbarMessage('Failed to subscribe. Please try again later.');
          setSnackbarOpen(true);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setSnackbarSeverity('error');
      setSnackbarMessage('Please enter a valid email address.');
      setSnackbarOpen(true);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
      },
    },
  };

  return (
    <Box
      ref={ref}
      sx={{
        pt: { xs: 8, md: 10 },
        pb: { xs: 4, md: 6 },
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container maxWidth="lg">
        <MotionBox
          sx={{
            textAlign: 'center',
            mb: 8,
            maxWidth: 800,
            mx: 'auto',
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeIn}
        >
          <MotionTypography
            variant="h4"
            fontWeight={700}
            sx={{
              mb: 2,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Justice Made Simple. For Everyone.
          </MotionTypography>
          <MotionTypography
            variant="h6"
            sx={{
              color: 'text.secondary',
              fontWeight: 400,
            }}
          >
            ClassAct connects individuals to class action lawsuits, streamlining
            enrollment, tracking case progress, and ensuring eligible users
            receive the compensation they deserve.
          </MotionTypography>
        </MotionBox>

        <MotionGrid
          container
          spacing={4}
          sx={{ mb: 4 }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerFeatures}
        >
          {features.map((feature, index) => (
            <MotionGrid
              size={{ xs: 12, md: 4 }}
              key={index}
              variants={featureItem}
            >
              <MotionPaper
                elevation={1}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: '16px',
                  border: '1px solid',
                  borderColor: theme.palette.grey[100],
                  backgroundColor: 'white',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    color: 'white',
                    mb: 3,
                    backgroundColor: theme.palette.primary.main,
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}
                >
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </MotionPaper>
            </MotionGrid>
          ))}
        </MotionGrid>

        {/* Added more visual interest to CTA section */}
        {/* Email signup form - similar to HeroSection */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: theme.palette.grey[100],
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h5"
            sx={{ mb: 3, fontWeight: 500, color: 'text.secondary' }}
          >
            Stay up to date on the latest from ClassAct
          </Typography>
          <motion.div
            variants={itemVariants}
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                width: '100%',
                maxWidth: 500,
                mb: 4,
              }}
            >
              <TextField
                fullWidth
                placeholder="Enter your email"
                variant="outlined"
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="primary" />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '12px',
                      bgcolor: 'background.paper',
                      height: '40px',
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                  },
                  '& .MuiInputBase-input:-webkit-autofill': {
                    WebkitTextFillColor: theme.palette.text.primary,
                    caretColor: theme.palette.text.primary,
                    borderRadius: 'inherit',
                    '-webkit-transition':
                      'background-color 5000s ease-in-out 0s',
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                sx={{
                  borderRadius: '20px',
                  px: 2,
                  py: 0,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  height: '40px',
                  minWidth: '130px',
                  whiteSpace: 'nowrap',
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Join Mailing List'}
              </Button>
            </Box>
          </motion.div>
        </Box>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '12px' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default PlaintiffsSection;
